<template>
  <v-row>
    <v-col cols="12">
      <v-card>
        <v-card-title color="rgba(0,0,0,0)" flat>
          <span class="font-weight-regular">LLM Model Management</span>
          <v-divider class="ml-2 mr-2" vertical></v-divider>
          <v-btn @click="initialize" icon title="Refresh">
            <v-icon dark>mdi-sync</v-icon>
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" class="mr-2" @click="openAddDialog">
            <v-icon left>mdi-plus</v-icon>
            Add Model
          </v-btn>
          <v-btn color="success" class="mr-2" @click="openBulkAddDialog">
            <v-icon left>mdi-plus-box-multiple</v-icon>
            Bulk Add
          </v-btn>
          <v-btn color="indigo" dark class="mr-2" @click="openManageEnginesDialog">
            <v-icon left>mdi-engine</v-icon>
            Manage Engines
          </v-btn>
          <v-btn color="blue-grey" dark class="mr-2" @click="openInheritDialog">
            <v-icon left>mdi-link-variant</v-icon>
            Docker Defaults
          </v-btn>
          <v-btn
            v-if="selectedItems.length > 0"
            color="warning"
            class="mr-2"
            @click="openBulkUpdateDialog"
          >
            <v-icon left>mdi-pencil-box-multiple</v-icon>
            Bulk Update ({{ selectedItems.length }})
          </v-btn>
          <v-btn
            v-if="selectedItems.length > 0"
            color="error"
            class="mr-2"
            @click="openBulkDeleteDialog"
          >
            <v-icon left>mdi-delete-sweep</v-icon>
            Bulk Delete ({{ selectedItems.length }})
          </v-btn>
          <v-btn
            v-if="selectedItems.length > 0"
            color="teal"
            dark
            class="mr-2"
            @click="openBulkAssignEngineDialog"
          >
            <v-icon left>mdi-engine</v-icon>
            Assign Engines ({{ selectedItems.length }})
          </v-btn>
          <!-- Status Toggle Buttons -->
          <v-menu v-if="selectedItems.length > 0" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="purple" dark class="mr-2" v-bind="attrs" v-on="on">
                <v-icon left>mdi-toggle-switch</v-icon>
                Set Status <v-icon right small>mdi-menu-down</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="bulkSetDisabled(true)">
                <v-list-item-icon>
                  <v-icon color="error">mdi-cancel</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Set Disabled</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item @click="bulkSetDisabled(false)">
                <v-list-item-icon>
                  <v-icon color="success">mdi-check-circle</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Set Active</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item @click="bulkSetGated(true)">
                <v-list-item-icon>
                  <v-icon color="warning">mdi-lock</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Set Gated</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item @click="bulkSetGated(false)">
                <v-list-item-icon>
                  <v-icon color="success">mdi-lock-open-variant</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Set Open (Not Gated)</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="secondary" v-bind="attrs" v-on="on">
                More <v-icon right>mdi-menu-down</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="openMigrateDialog">
                <v-list-item-icon>
                  <v-icon color="warning">mdi-database-arrow-right</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Migrate to New Structure</v-list-item-title>
                  <v-list-item-subtitle>Convert ModelTags to LlmTags</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item @click="openSparkrunAllowlistDialog">
                <v-list-item-icon>
                  <v-icon color="indigo">mdi-server-network</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Sparkrun Engine Allowlist</v-list-item-title>
                  <v-list-item-subtitle>Select engines for multi-node inference</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-card-title>

        <v-card-text>
          <!-- Filters -->
          <v-row class="mb-4">
            <v-col cols="12" md="3">
              <v-text-field
                v-model="searchText"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
                dense
                outlined
                clearable
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="2">
              <v-autocomplete
                v-model="filterEngine"
                :items="[{ id: null, name: 'All Engines' }, ...engines]"
                item-text="name"
                item-value="id"
                label="Filter by Engine"
                dense
                outlined
                hide-details
                clearable
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="2">
              <v-select
                v-model="filterGated"
                :items="gatedOptions"
                item-text="text"
                item-value="value"
                label="Gated"
                dense
                outlined
                hide-details
              ></v-select>
            </v-col>
            <v-col cols="12" md="2">
              <v-select
                v-model="filterDisabled"
                :items="disabledOptions"
                item-text="text"
                item-value="value"
                label="Status"
                dense
                outlined
                hide-details
              ></v-select>
            </v-col>
          </v-row>

          <!-- Data Table -->
          <v-data-table
            v-model="selectedItems"
            :headers="tableHeaders"
            :items="llmTags"
            :server-items-length="pagination.total"
            :options.sync="tableOptions"
            :loading="tableLoading"
            :footer-props="{
              'items-per-page-options': [10, 15, 25, 50, 100]
            }"
            class="elevation-1"
            show-select
            item-key="id"
            @update:options="onTableOptionsChange"
          >
            <template v-slot:item.name="{ item }">
              <div>
                <strong>{{ item.name }}</strong>
              </div>
            </template>

            <template v-slot:item.engines="{ item }">
              <div v-if="item.engine_configurations && item.engine_configurations.length > 0">
                <v-chip
                  v-for="config in item.engine_configurations"
                  :key="config.id"
                  small
                  color="primary"
                  dark
                  class="mr-1 mb-1"
                >
                  {{ config.engine_name }}
                </v-chip>
              </div>
              <span v-else class="grey--text">No engines</span>
            </template>

            <template v-slot:item.parameters="{ item }">
              <v-chip v-if="item.parameters" small color="blue" outlined>
                {{ item.parameters }}
              </v-chip>
              <span v-else>-</span>
            </template>

            <template v-slot:item.quantization="{ item }">
              <v-chip v-if="item.quantization" small color="orange" outlined>
                {{ item.quantization }}
              </v-chip>
              <span v-else>-</span>
            </template>

            <template v-slot:item.size="{ item }">
              <span v-if="item.size">{{ formatSize(item.size) }}</span>
              <span v-else>-</span>
            </template>

            <template v-slot:item.downloads="{ item }">
              <span>{{ item.downloads || 0 }}</span>
            </template>

            <template v-slot:item.likes="{ item }">
              <span>{{ item.likes || 0 }}</span>
            </template>

            <template v-slot:item.trending_score="{ item }">
              <span>{{ item.trending_score ? item.trending_score.toFixed(2) : '0' }}</span>
            </template>

            <template v-slot:item.created_at="{ item }">
              <span v-if="item.created_at">{{ new Date(item.created_at).toLocaleDateString() }}</span>
              <span v-else>-</span>
            </template>

            <template v-slot:item.gated="{ item }">
              <v-icon v-if="item.gated" small color="warning">mdi-lock</v-icon>
              <v-icon v-else small color="success">mdi-lock-open-variant</v-icon>
            </template>

            <template v-slot:item.is_disabled="{ item }">
              <v-chip v-if="item.is_disabled" small color="error" dark>Disabled</v-chip>
              <v-chip v-else small color="success" dark>Active</v-chip>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn icon small color="primary" @click="openEditDialog(item)" title="Edit">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon small color="info" @click="openEngineConfigDialog(item)" title="Engine Configs">
                <v-icon small>mdi-cog</v-icon>
              </v-btn>
              <v-btn icon small color="error" @click="openDeleteDialog(item)" title="Delete">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- Add/Edit Dialog -->
      <v-dialog v-model="showFormDialog" max-width="800px" persistent>
        <v-card>
          <v-card-title>
            <span class="text-h5">{{ isEditMode ? 'Edit Model' : 'Add New Model' }}</span>
          </v-card-title>
          <v-card-text>
            <v-form ref="form" v-model="isFormValid">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="formData.name"
                    label="Model Name / Path *"
                    outlined
                    dense
                    :rules="[rules.required]"
                    placeholder="e.g., google/gemma-3-1b-it"
                    hint="This is the unique identifier for the model"
                    persistent-hint
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="formData.sizeInput"
                    label="Size"
                    outlined
                    dense
                    placeholder="e.g., 2.5GB, 500MB"
                    :hint="getSizeHint(formData.sizeInput)"
                    persistent-hint
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="formData.parameters"
                    label="Parameters"
                    outlined
                    dense
                    placeholder="e.g., 7B, 500M, 1.5B"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="formData.quantization"
                    label="Quantization"
                    outlined
                    dense
                    placeholder="e.g., Q4_K_M, FP16, INT8"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="formData.downloads"
                    label="Downloads"
                    outlined
                    dense
                    type="number"
                    placeholder="0"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="formData.likes"
                    label="Likes"
                    outlined
                    dense
                    type="number"
                    placeholder="0"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="formData.trending_score"
                    label="Trending Score"
                    outlined
                    dense
                    type="number"
                    step="0.01"
                    placeholder="0.0"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="formData.created_at"
                    label="Created At (Last Modified)"
                    outlined
                    dense
                    type="datetime-local"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-checkbox
                    v-model="formData.gated"
                    label="Gated Model (Requires authentication)"
                    dense
                    hide-details
                  ></v-checkbox>
                </v-col>
                <v-col cols="12" md="6">
                  <v-checkbox
                    v-model="formData.is_disabled"
                    label="Disabled"
                    dense
                    hide-details
                  ></v-checkbox>
                </v-col>
              </v-row>

              <!-- Engine Selection (only for new models) -->
              <v-divider class="my-4" v-if="!isEditMode"></v-divider>
              <div v-if="!isEditMode">
                <h4 class="mb-3">Assign to Engines</h4>
                <v-alert type="info" dense text class="mb-3">
                  Select one or more engines to create engine configurations for this model.
                </v-alert>
                <v-autocomplete
                  v-model="formData.selectedEngines"
                  :items="engines"
                  item-text="name"
                  item-value="id"
                  label="Select Engines"
                  outlined
                  dense
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  placeholder="Select engines..."
                ></v-autocomplete>
              </div>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="closeFormDialog">Cancel</v-btn>
            <v-btn color="primary" :disabled="!isFormValid" @click="submitForm" :loading="formLoading">
              {{ isEditMode ? 'Update' : 'Create' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" max-width="500px">
        <v-card>
          <v-card-title class="text-h5 error white--text">
            <v-icon color="white" class="mr-2">mdi-delete</v-icon>
            Confirm Delete
          </v-card-title>
          <v-card-text class="pt-4">
            <p>Are you sure you want to delete <strong>{{ deleteItemName }}</strong>?</p>
            <v-alert type="warning" dense outlined>
              This will also delete all engine configurations associated with this model.
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="showDeleteDialog = false">Cancel</v-btn>
            <v-btn color="error" @click="confirmDelete" :loading="deleteLoading">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Bulk Add Dialog -->
      <v-dialog v-model="showBulkAddDialog" max-width="1200px" persistent>
        <v-card>
          <v-card-title>
            <span class="text-h5">Bulk Add Models</span>
            <v-spacer></v-spacer>
            <v-btn icon @click="showBulkAddDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-alert type="info" dense outlined class="mb-4">
              Add multiple models at once. Each model can be assigned to multiple engines.
            </v-alert>

            <!-- Engine Selection for All -->
            <v-autocomplete
              v-model="bulkSelectedEngines"
              :items="engines"
              item-text="name"
              item-value="id"
              label="Assign all models to these engines"
              outlined
              dense
              multiple
              chips
              small-chips
              deletable-chips
              class="mb-4"
            ></v-autocomplete>

            <div v-for="(item, index) in bulkItems" :key="index" class="bulk-item-row mb-3">
              <v-row dense align="center">
                <v-col cols="3">
                  <v-text-field
                    v-model="item.name"
                    label="Model Name/Path *"
                    outlined
                    dense
                    hide-details
                    placeholder="e.g., google/gemma-3-1b-it"
                  ></v-text-field>
                </v-col>
                <v-col cols="2">
                  <v-text-field
                    v-model="item.sizeInput"
                    label="Size"
                    outlined
                    dense
                    hide-details
                    placeholder="e.g., 2.5GB"
                  ></v-text-field>
                </v-col>
                <v-col cols="2">
                  <v-text-field
                    v-model="item.parameters"
                    label="Parameters"
                    outlined
                    dense
                    hide-details
                    placeholder="e.g., 7B"
                  ></v-text-field>
                </v-col>
                <v-col cols="2">
                  <v-text-field
                    v-model="item.quantization"
                    label="Quantization"
                    outlined
                    dense
                    hide-details
                    placeholder="e.g., Q4_K_M"
                  ></v-text-field>
                </v-col>
                <v-col cols="1" class="d-flex align-center">
                  <v-checkbox
                    v-model="item.gated"
                    label="Gated"
                    dense
                    hide-details
                  ></v-checkbox>
                </v-col>
                <v-col cols="1">
                  <v-text-field
                    v-model="item.likes"
                    label="Likes"
                    outlined
                    dense
                    hide-details
                    type="number"
                    placeholder="0"
                  ></v-text-field>
                </v-col>
                <v-col cols="1" class="d-flex align-center justify-center">
                  <v-btn icon color="error" @click="removeBulkItem(index)" :disabled="bulkItems.length === 1">
                    <v-icon>mdi-minus-circle</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </div>

            <v-btn text color="primary" @click="addBulkItem" class="mb-4">
              <v-icon left>mdi-plus</v-icon>
              Add Another Model
            </v-btn>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="showBulkAddDialog = false">Cancel</v-btn>
            <v-btn
              color="success"
              @click="submitBulkAdd"
              :disabled="!canSubmitBulkAdd"
              :loading="bulkAddLoading"
            >
              <v-icon left>mdi-check-all</v-icon>
              Create All
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Bulk Update Dialog -->
      <v-dialog v-model="showBulkUpdateDialog" max-width="1400px" persistent>
        <v-card>
          <v-card-title>
            <span class="text-h5">Bulk Update Models ({{ bulkUpdateItems.length }} selected)</span>
            <v-spacer></v-spacer>
            <v-btn icon @click="showBulkUpdateDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <div class="bulk-update-container" style="max-height: 500px; overflow-y: auto;">
              <div v-for="(item) in bulkUpdateItems" :key="item.id" class="bulk-item-row mb-3">
                <v-row dense align="center">
                  <v-col cols="3">
                    <v-text-field
                      v-model="item.name"
                      label="Model Name *"
                      outlined
                      dense
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="2">
                    <v-text-field
                      v-model="item.sizeInput"
                      label="Size"
                      outlined
                      dense
                      hide-details
                      placeholder="e.g., 2.5GB"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="2">
                    <v-text-field
                      v-model="item.parameters"
                      label="Parameters"
                      outlined
                      dense
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="2">
                    <v-text-field
                      v-model="item.quantization"
                      label="Quantization"
                      outlined
                      dense
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="1" class="d-flex align-center">
                    <v-checkbox
                      v-model="item.gated"
                      label="Gated"
                      dense
                      hide-details
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="1" class="d-flex align-center">
                    <v-checkbox
                      v-model="item.is_disabled"
                      label="Disabled"
                      dense
                      hide-details
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="1">
                    <v-text-field
                      v-model="item.likes"
                      label="Likes"
                      outlined
                      dense
                      hide-details
                      type="number"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="1">
                    <v-text-field
                      v-model="item.trending_score"
                      label="Trending"
                      outlined
                      dense
                      hide-details
                      type="number"
                      step="0.01"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </div>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="showBulkUpdateDialog = false">Cancel</v-btn>
            <v-btn color="primary" @click="submitBulkUpdate" :loading="bulkUpdateLoading">
              <v-icon left>mdi-content-save-all</v-icon>
              Update All
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Bulk Delete Dialog -->
      <v-dialog v-model="showBulkDeleteDialog" max-width="600px" persistent>
        <v-card>
          <v-card-title class="text-h5 error white--text">
            <v-icon color="white" class="mr-2">mdi-delete-sweep</v-icon>
            Bulk Delete Models
          </v-card-title>
          <v-card-text class="pt-4">
            <v-alert type="error" dense text class="mb-4">
              <strong>Warning:</strong> This action cannot be undone. You are about to delete {{ selectedItems.length }} model(s) and their engine configurations.
            </v-alert>
            <div style="max-height: 300px; overflow-y: auto;">
              <v-list dense>
                <v-list-item v-for="item in selectedItems" :key="item.id">
                  <v-list-item-icon>
                    <v-icon color="error">mdi-tag-remove</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="showBulkDeleteDialog = false">Cancel</v-btn>
            <v-btn color="error" @click="submitBulkDelete" :loading="bulkDeleteLoading">
              <v-icon left>mdi-delete</v-icon>
              Delete All ({{ selectedItems.length }})
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Engine Configuration Dialog -->
      <v-dialog v-model="showEngineConfigDialog" max-width="900px" persistent>
        <v-card>
          <v-card-title>
            <span class="text-h5">Engine Configurations - {{ currentConfigItem?.name }}</span>
            <v-spacer></v-spacer>
            <v-btn icon @click="showEngineConfigDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <!-- Existing Configurations -->
            <div v-if="currentConfigurations.length > 0" class="mb-4">
              <h4 class="mb-2">Current Configurations</h4>
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>Engine</th>
                      <th>Env Variables</th>
                      <th>Engine Args</th>
                      <th>Docker Options</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="config in currentConfigurations" :key="config.id">
                      <td>
                        <v-chip small color="primary" dark>{{ config.engine_name }}</v-chip>
                      </td>
                      <td>{{ (config.environment_variables || []).length }} vars</td>
                      <td>{{ (config.engine_arguments || []).length }} args</td>
                      <td>{{ (config.docker_options || []).length }} options</td>
                      <td>
                        <v-btn icon small color="error" @click="deleteEngineConfig(config.id)">
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>

            <v-divider class="my-4"></v-divider>

            <!-- Add New Configuration -->
            <h4 class="mb-2">Add Engine Configuration</h4>
            <v-autocomplete
              v-model="newConfigEngineId"
              :items="availableEnginesForConfig"
              item-text="name"
              item-value="id"
              label="Select Engine"
              outlined
              dense
              placeholder="Select an engine to add..."
            ></v-autocomplete>
            <v-btn
              color="primary"
              @click="addEngineConfig"
              :disabled="!newConfigEngineId"
              :loading="addConfigLoading"
            >
              <v-icon left>mdi-plus</v-icon>
              Add Configuration
            </v-btn>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="showEngineConfigDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Bulk Assign Engine Dialog -->
      <v-dialog v-model="showBulkAssignEngineDialog" max-width="600px" persistent>
        <v-card>
          <v-card-title>
            <span class="text-h5">Bulk Assign Engines ({{ selectedItems.length }} models)</span>
            <v-spacer></v-spacer>
            <v-btn icon @click="showBulkAssignEngineDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-alert type="info" dense outlined class="mb-4">
              Select one or more engines to assign to the selected models. Existing engine-model combinations will be skipped automatically.
            </v-alert>
            <v-autocomplete
              v-model="bulkAssignEngineIds"
              :items="engines"
              item-text="name"
              item-value="id"
              label="Select Engines"
              outlined
              dense
              multiple
              chips
              small-chips
              deletable-chips
              placeholder="Select engines to assign..."
            ></v-autocomplete>
            <div style="max-height: 250px; overflow-y: auto;">
              <v-list dense>
                <v-list-item v-for="item in selectedItems" :key="item.id">
                  <v-list-item-icon>
                    <v-icon color="teal">mdi-tag</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="showBulkAssignEngineDialog = false">Cancel</v-btn>
            <v-btn
              color="teal"
              dark
              @click="submitBulkAssignEngines"
              :disabled="bulkAssignEngineIds.length === 0"
              :loading="bulkAssignEngineLoading"
            >
              <v-icon left>mdi-check-all</v-icon>
              Assign Engines
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Manage Engines Dialog -->
      <v-dialog v-model="showManageEnginesDialog" max-width="700px" persistent>
        <v-card>
          <v-card-title>
            <span class="text-h5">Manage Engines</span>
            <v-spacer></v-spacer>
            <v-btn icon @click="closeManageEnginesDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <!-- Add / Edit Engine Form -->
            <h4 class="mb-2">{{ engineForm.id ? 'Edit Engine' : 'Add New Engine' }}</h4>
            <v-row dense align="center">
              <v-col cols="12" md="5">
                <v-text-field
                  v-model="engineForm.name"
                  label="Engine Name *"
                  outlined
                  dense
                  hide-details
                  placeholder="e.g., vLLM, Ollama"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="5">
                <v-file-input
                  v-model="engineForm.icon"
                  label="Icon (optional)"
                  outlined
                  dense
                  hide-details
                  accept="image/*"
                  prepend-icon="mdi-image"
                ></v-file-input>
              </v-col>
              <v-col cols="12" md="2" class="d-flex align-center">
                <v-btn
                  color="primary"
                  block
                  :disabled="!engineForm.name"
                  :loading="engineFormLoading"
                  @click="submitEngineForm"
                >
                  {{ engineForm.id ? 'Update' : 'Add' }}
                </v-btn>
              </v-col>
            </v-row>
            <v-btn
              v-if="engineForm.id"
              text
              small
              color="grey"
              class="mt-1"
              @click="resetEngineForm"
            >
              <v-icon left small>mdi-close</v-icon>
              Cancel edit
            </v-btn>

            <v-divider class="my-4"></v-divider>

            <!-- Existing Engines -->
            <h4 class="mb-2">Existing Engines ({{ engines.length }})</h4>
            <div style="max-height: 350px; overflow-y: auto;">
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th style="width: 70px;">ID</th>
                      <th>Name</th>
                      <th style="width: 110px;">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="engine in engines" :key="engine.id">
                      <td>{{ engine.id }}</td>
                      <td>{{ engine.name }}</td>
                      <td>
                        <v-btn icon small color="primary" @click="editEngine(engine)" title="Edit">
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          color="error"
                          :loading="engineDeleteLoadingId === engine.id"
                          @click="deleteEngine(engine)"
                          title="Delete"
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                    <tr v-if="engines.length === 0">
                      <td colspan="3" class="text-center grey--text">No engines yet</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="closeManageEnginesDialog">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Migrate to New Structure Dialog -->
      <v-dialog v-model="showMigrateDialog" max-width="600px" persistent>
        <v-card>
          <v-card-title class="text-h5 warning white--text">
            <v-icon color="white" class="mr-2">mdi-database-arrow-right</v-icon>
            Migrate to New Structure
          </v-card-title>
          <v-card-text class="pt-4">
            <v-alert type="warning" dense text class="mb-4">
              <strong>Important:</strong> This will migrate data from the old structure (ModelTags) to the new structure (LlmTags + EngineLlmTagConfigurations).
            </v-alert>
            <v-alert type="info" dense outlined class="mb-4">
              <strong>What will happen:</strong>
              <ul class="mt-2">
                <li>Each ModelTag will be converted to an LlmTag</li>
                <li>Engine-specific configurations will be created in EngineLlmTagConfigurations</li>
                <li>Both standard and Jetson configurations will be migrated</li>
                <li>Existing LlmTags with the same name will be reused</li>
              </ul>
            </v-alert>
            <p class="text-body-2">This operation is safe to run multiple times. Duplicate entries will be skipped.</p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="showMigrateDialog = false">Cancel</v-btn>
            <v-btn color="warning" @click="submitMigration" :loading="migrateLoading">
              <v-icon left>mdi-database-arrow-right</v-icon>
              Start Migration
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="showSparkrunAllowlistDialog" max-width="600px" persistent>
        <v-card>
          <v-card-title class="text-h5 indigo white--text">
            <v-icon color="white" class="mr-2">mdi-server-network</v-icon>
            Sparkrun Engine Allowlist
          </v-card-title>
          <v-card-text class="pt-4">
            <p class="text-body-2 mb-4">
              Select which LLM engines are allowed for multi-node (sparkrun) inference.
              Only applications with a supported engine will show the "Multi-Node" button.
            </p>
            <v-progress-linear v-if="sparkrunAllowlistLoading" indeterminate color="indigo" class="mb-2"></v-progress-linear>
            <v-list v-else dense>
              <v-list-item v-for="engine in sparkrunEngines" :key="engine.id">
                <v-list-item-action>
                  <v-switch
                    v-model="engine.is_supported"
                    color="indigo"
                    hide-details
                  ></v-switch>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{ engine.name }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            <v-alert v-if="sparkrunEngines.length === 0 && !sparkrunAllowlistLoading" type="info" dense text>
              No engines found. Add engines first.
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="showSparkrunAllowlistDialog = false">Cancel</v-btn>
            <v-btn color="indigo" @click="saveSparkrunAllowlist" :loading="sparkrunAllowlistSaving">
              <v-icon left>mdi-content-save</v-icon>
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Inherit Docker defaults dialog -->
      <v-dialog v-model="showInheritDialog" max-width="620px" persistent>
        <v-card>
          <v-card-title>
            <span class="text-h5">Docker Defaults</span>
            <v-spacer></v-spacer>
            <v-btn icon @click="showInheritDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <p class="mb-4">
              A model's environment variables, engine arguments and container options come from
              its engine's Docker unless the model sets its own. Models imported before this rule
              carry a copy of the Docker's values from that day, so later Docker edits do not
              reach them. This makes them follow the Docker again.
            </p>
            <v-select
              v-model="inheritEngineId"
              :items="engines"
              item-text="name"
              item-value="id"
              label="Engine"
              outlined
              dense
              @change="previewInherit"
            ></v-select>
            <v-radio-group v-model="inheritMode" class="mt-0" @change="previewInherit">
              <v-radio value="copies" label="Only exact copies of the Docker values (safe, nothing visible changes)"></v-radio>
              <v-radio value="all" label="Reset every model to the Docker values (removes per-model values)"></v-radio>
            </v-radio-group>
            <v-progress-linear v-if="inheritLoading" indeterminate class="mb-2"></v-progress-linear>
            <v-alert v-if="inheritPreview" :type="inheritMode === 'all' && inheritPreview.custom_rows ? 'warning' : 'info'" dense text>
              <div>{{ inheritPreview.total }} model(s) on this engine.</div>
              <div>{{ inheritPreview.copies_rows }} carry an exact copy of the Docker values.</div>
              <div>{{ inheritPreview.custom_rows }} have values of their own{{ inheritMode === 'all' ? ' -- these will be removed' : ' -- these are kept' }}.</div>
              <div class="font-weight-bold mt-1">{{ inheritPreview.changed_rows }} model(s) will change.</div>
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="showInheritDialog = false">Cancel</v-btn>
            <v-btn
              :color="inheritMode === 'all' ? 'error' : 'primary'"
              :disabled="!inheritPreview || !inheritPreview.changed_rows"
              :loading="inheritLoading"
              @click="applyInherit"
            >
              Apply
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-col>
  </v-row>
</template>

<script>
import { apiService } from '../../service/api.service'
import Swal from 'sweetalert2'

export default {
  name: 'ModelsTable',
  data: () => ({
    // Data
    llmTags: [],
    engines: [],
    selectedItems: [],
    tableLoading: false,

    // Pagination
    pagination: {
      page: 1,
      per_page: 15,
      total: 0,
      total_pages: 0,
      has_next: false,
      has_prev: false
    },
    tableOptions: {
      page: 1,
      itemsPerPage: 15
    },
    searchTimeout: null,

    // Search and filters
    searchText: '',
    filterEngine: null,
    filterGated: null,
    filterDisabled: null,

    gatedOptions: [
      { text: 'All', value: null },
      { text: 'Gated', value: true },
      { text: 'Open', value: false }
    ],
    disabledOptions: [
      { text: 'All', value: null },
      { text: 'Active', value: false },
      { text: 'Disabled', value: true }
    ],

    // Table headers
    tableHeaders: [
      { text: 'ID', value: 'id', width: '70px' },
      { text: 'Name', value: 'name' },
      { text: 'Engines', value: 'engines', sortable: false },
      { text: 'Parameters', value: 'parameters' },
      { text: 'Quantization', value: 'quantization' },
      { text: 'Size', value: 'size' },
      { text: 'Downloads', value: 'downloads' },
      { text: 'Likes', value: 'likes' },
      { text: 'Trending', value: 'trending_score' },
      { text: 'Created At', value: 'created_at' },
      { text: 'Gated', value: 'gated', width: '80px' },
      { text: 'Status', value: 'is_disabled', width: '100px' },
      { text: 'Actions', value: 'actions', sortable: false, width: '130px' }
    ],

    // Form dialog
    showFormDialog: false,
    isFormValid: false,
    isEditMode: false,
    formLoading: false,
    currentEditItem: null,
    formData: {
      name: '',
      sizeInput: '',
      parameters: '',
      quantization: '',
      downloads: 0,
      likes: 0,
      trending_score: 0,
      created_at: '',
      gated: false,
      is_disabled: false,
      selectedEngines: []
    },

    // Delete dialog
    showDeleteDialog: false,
    deleteItem: null,
    deleteItemName: '',
    deleteLoading: false,

    // Bulk add dialog
    showBulkAddDialog: false,
    bulkAddLoading: false,
    bulkSelectedEngines: [],
    bulkItems: [{ name: '', sizeInput: '', parameters: '', quantization: '', gated: false, likes: 0, trending_score: 0, created_at: '' }],

    // Bulk update dialog
    showBulkUpdateDialog: false,
    bulkUpdateLoading: false,
    bulkUpdateItems: [],

    // Bulk delete dialog
    showBulkDeleteDialog: false,
    bulkDeleteLoading: false,

    // Engine config dialog
    showEngineConfigDialog: false,
    currentConfigItem: null,
    currentConfigurations: [],
    newConfigEngineId: null,
    addConfigLoading: false,

    // Bulk assign engine dialog
    showBulkAssignEngineDialog: false,
    bulkAssignEngineLoading: false,
    bulkAssignEngineIds: [],

    // Inherit Docker defaults dialog
    showInheritDialog: false,
    inheritEngineId: null,
    inheritMode: 'copies',
    inheritPreview: null,
    inheritLoading: false,

    // Manage engines dialog
    showManageEnginesDialog: false,
    engineFormLoading: false,
    engineDeleteLoadingId: null,
    engineForm: {
      id: null,
      name: '',
      icon: null
    },

    // Migrate dialog
    showMigrateDialog: false,
    migrateLoading: false,

    // Sparkrun allowlist dialog
    showSparkrunAllowlistDialog: false,
    sparkrunAllowlistLoading: false,
    sparkrunAllowlistSaving: false,
    sparkrunEngines: [],

    // Validation rules
    rules: {
      required: (value) => !!value || 'This field is required.'
    }
  }),

  async mounted () {
    await this.initialize()
  },

  watch: {
    searchText () {
      // Debounce search
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.tableOptions.page = 1
        this.loadLlmTags()
      }, 500)
    },
    filterEngine () {
      this.tableOptions.page = 1
      this.loadLlmTags()
    },
    filterGated () {
      this.tableOptions.page = 1
      this.loadLlmTags()
    },
    filterDisabled () {
      this.tableOptions.page = 1
      this.loadLlmTags()
    }
  },

  computed: {
    canSubmitBulkAdd () {
      return this.bulkItems.some(item => item.name)
    },

    availableEnginesForConfig () {
      if (!this.currentConfigItem) return this.engines
      const usedEngineIds = this.currentConfigurations.map(c => c.engine_id)
      return this.engines.filter(e => !usedEngineIds.includes(e.id))
    }
  },

  methods: {
    async initialize () {
      try {
        // Load engines
        const enginesResponse = await apiService.get('/admin/engines')
        this.engines = enginesResponse.data.engines || []

        // Load LLM Tags with pagination
        await this.loadLlmTags()
      } catch (error) {
        console.error('Failed to load data:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load data'
        })
      }
    },

    async loadLlmTags () {
      this.tableLoading = true
      try {
        const params = new URLSearchParams()
        params.append('page', this.tableOptions.page)
        params.append('per_page', this.tableOptions.itemsPerPage)

        if (this.searchText) {
          params.append('search', this.searchText)
        }
        if (this.filterEngine) {
          params.append('engine_id', this.filterEngine)
        }
        if (this.filterGated !== null) {
          params.append('gated', this.filterGated)
        }
        if (this.filterDisabled !== null) {
          params.append('is_disabled', this.filterDisabled)
        }

        const response = await apiService.get(`/admin/llm-tags?${params.toString()}`)
        this.llmTags = response.data.llm_tags || []
        this.pagination = response.data.pagination || this.pagination
      } catch (error) {
        console.error('Failed to load LLM tags:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load data'
        })
      } finally {
        this.tableLoading = false
      }
    },

    onTableOptionsChange (options) {
      this.tableOptions = { ...options }
      this.loadLlmTags()
    },

    // Utility methods
    formatSize (sizeInGB) {
      if (!sizeInGB) return '-'
      if (sizeInGB >= 1000) return `${(sizeInGB / 1000).toFixed(2)} TB`
      if (sizeInGB >= 1) return `${sizeInGB.toFixed(2)} GB`
      return `${(sizeInGB * 1000).toFixed(2)} MB`
    },

    formatSizeToInput (sizeInGB) {
      if (!sizeInGB) return ''
      if (sizeInGB >= 1000) return `${(sizeInGB / 1000).toFixed(2)}TB`
      if (sizeInGB >= 1) return `${sizeInGB}GB`
      return `${(sizeInGB * 1000).toFixed(0)}MB`
    },

    parseSize (sizeStr) {
      if (!sizeStr) return 0
      const str = sizeStr.toString().toUpperCase().trim()
      const match = str.match(/^([\d.]+)\s*(TB|GB|MB|KB)?$/)
      if (!match) return parseFloat(sizeStr) || 0

      const num = parseFloat(match[1])
      const suffix = match[2] || 'GB'

      switch (suffix) {
        case 'TB': return num * 1000
        case 'GB': return num
        case 'MB': return num / 1000
        case 'KB': return num / 1000000
        default: return num
      }
    },

    getSizeHint (sizeStr) {
      if (!sizeStr) return ''
      const sizeInGB = this.parseSize(sizeStr)
      if (sizeInGB > 0) return `= ${sizeInGB} GB`
      return ''
    },

    // Form dialog methods
    openAddDialog () {
      this.isEditMode = false
      this.currentEditItem = null
      this.formData = {
        name: '',
        sizeInput: '',
        parameters: '',
        quantization: '',
        downloads: 0,
        likes: 0,
        trending_score: 0,
        created_at: '',
        gated: false,
        is_disabled: false,
        selectedEngines: []
      }
      this.showFormDialog = true
    },

    openEditDialog (item) {
      this.isEditMode = true
      this.currentEditItem = item
      this.formData = {
        name: item.name,
        sizeInput: item.size ? this.formatSizeToInput(item.size) : '',
        parameters: item.parameters || '',
        quantization: item.quantization || '',
        downloads: item.downloads || 0,
        likes: item.likes || 0,
        trending_score: item.trending_score || 0,
        created_at: item.created_at ? new Date(item.created_at).toISOString().slice(0, 16) : '',
        gated: item.gated || false,
        is_disabled: item.is_disabled || false,
        selectedEngines: []
      }
      this.showFormDialog = true
    },

    closeFormDialog () {
      this.showFormDialog = false
      this.formData = {
        name: '',
        sizeInput: '',
        parameters: '',
        quantization: '',
        downloads: 0,
        likes: 0,
        trending_score: 0,
        created_at: '',
        gated: false,
        is_disabled: false,
        selectedEngines: []
      }
      if (this.$refs.form) {
        this.$refs.form.reset()
      }
    },

    async submitForm () {
      this.formLoading = true
      try {
        const payload = {
          name: this.formData.name,
          size: this.parseSize(this.formData.sizeInput),
          parameters: this.formData.parameters,
          quantization: this.formData.quantization,
          downloads: parseInt(this.formData.downloads) || 0,
          likes: parseInt(this.formData.likes) || 0,
          trending_score: parseFloat(this.formData.trending_score) || 0,
          created_at: this.formData.created_at ? new Date(this.formData.created_at).toISOString() : null,
          gated: this.formData.gated,
          is_disabled: this.formData.is_disabled
        }

        if (this.isEditMode) {
          await apiService.put(`/admin/llm-tags/${this.currentEditItem.id}`, payload)
        } else {
          // Create the tag with engine configurations in a single request
          await apiService.post('/admin/llm-tags/bulk', {
            items: [payload],
            engine_ids: this.formData.selectedEngines
          })
        }

        this.closeFormDialog()
        await this.initialize()
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: this.isEditMode ? 'Model updated successfully' : 'Model created successfully'
        })
      } catch (error) {
        console.error('Form submit error:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to save model'
        })
      } finally {
        this.formLoading = false
      }
    },

    // Delete methods
    openDeleteDialog (item) {
      this.deleteItem = item
      this.deleteItemName = item.name
      this.showDeleteDialog = true
    },

    async confirmDelete () {
      this.deleteLoading = true
      try {
        await apiService.delete(`/admin/llm-tags/${this.deleteItem.id}`)
        this.showDeleteDialog = false
        await this.initialize()
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Model deleted successfully'
        })
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete model'
        })
      } finally {
        this.deleteLoading = false
      }
    },

    // Bulk add methods
    openBulkAddDialog () {
      this.bulkSelectedEngines = []
      this.bulkItems = [{ name: '', sizeInput: '', parameters: '', quantization: '', gated: false, likes: 0, trending_score: 0, created_at: '' }]
      this.showBulkAddDialog = true
    },

    addBulkItem () {
      this.bulkItems.push({ name: '', sizeInput: '', parameters: '', quantization: '', gated: false, likes: 0, trending_score: 0, created_at: '' })
    },

    removeBulkItem (index) {
      this.bulkItems.splice(index, 1)
    },

    async submitBulkAdd () {
      this.bulkAddLoading = true
      try {
        const items = this.bulkItems
          .filter(item => item.name)
          .map(item => ({
            name: item.name,
            size: this.parseSize(item.sizeInput),
            parameters: item.parameters,
            quantization: item.quantization,
            gated: item.gated,
            is_disabled: false,
            likes: parseInt(item.likes) || 0,
            trending_score: parseFloat(item.trending_score) || 0,
            created_at: item.created_at ? new Date(item.created_at).toISOString() : null
          }))

        if (items.length === 0) {
          Swal.fire({ icon: 'warning', title: 'Warning', text: 'No valid items to add' })
          return
        }

        const response = await apiService.post('/admin/llm-tags/bulk', {
          items,
          engine_ids: this.bulkSelectedEngines
        })

        this.showBulkAddDialog = false
        await this.initialize()

        const createdCount = response.data.created_count || 0
        const errors = response.data.errors || []

        if (errors.length > 0) {
          Swal.fire({
            icon: 'warning',
            title: 'Partially Completed',
            html: `Created ${createdCount} models.<br><br>Errors:<br>${errors.join('<br>')}`
          })
        } else {
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: `Successfully created ${createdCount} models`
          })
        }
      } catch (error) {
        console.error('Bulk add error:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to create models'
        })
      } finally {
        this.bulkAddLoading = false
      }
    },

    // Bulk update methods
    openBulkUpdateDialog () {
      this.bulkUpdateItems = this.selectedItems.map(item => ({
        id: item.id,
        name: item.name,
        sizeInput: item.size ? this.formatSizeToInput(item.size) : '',
        parameters: item.parameters || '',
        quantization: item.quantization || '',
        gated: item.gated || false,
        is_disabled: item.is_disabled || false,
        likes: item.likes || 0,
        trending_score: item.trending_score || 0,
        created_at: item.created_at ? new Date(item.created_at).toISOString().slice(0, 16) : ''
      }))
      this.showBulkUpdateDialog = true
    },

    async submitBulkUpdate () {
      this.bulkUpdateLoading = true
      try {
        const items = this.bulkUpdateItems.map(item => ({
          id: item.id,
          name: item.name,
          size: this.parseSize(item.sizeInput),
          parameters: item.parameters,
          quantization: item.quantization,
          gated: item.gated,
          is_disabled: item.is_disabled,
          likes: parseInt(item.likes) || 0,
          trending_score: parseFloat(item.trending_score) || 0,
          created_at: item.created_at ? new Date(item.created_at).toISOString() : null
        }))

        await apiService.put('/admin/llm-tags/bulk', { items })

        this.showBulkUpdateDialog = false
        this.selectedItems = []
        await this.initialize()
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: `Successfully updated ${this.bulkUpdateItems.length} models`
        })
      } catch (error) {
        console.error('Bulk update error:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to update models'
        })
      } finally {
        this.bulkUpdateLoading = false
      }
    },

    // Bulk delete methods
    openBulkDeleteDialog () {
      this.showBulkDeleteDialog = true
    },

    async submitBulkDelete () {
      this.bulkDeleteLoading = true
      try {
        const ids = this.selectedItems.map(item => item.id)
        await apiService.delete('/admin/llm-tags/bulk', { data: { ids } })

        this.showBulkDeleteDialog = false
        this.selectedItems = []
        await this.initialize()
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Models deleted successfully'
        })
      } catch (error) {
        console.error('Bulk delete error:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to delete models'
        })
      } finally {
        this.bulkDeleteLoading = false
      }
    },

    // Engine config methods
    openEngineConfigDialog (item) {
      this.currentConfigItem = item
      this.currentConfigurations = item.engine_configurations || []
      this.newConfigEngineId = null
      this.showEngineConfigDialog = true
    },

    async addEngineConfig () {
      this.addConfigLoading = true
      try {
        await apiService.post('/admin/engine-llm-tag-configurations', {
          engine_id: this.newConfigEngineId,
          tag_id: this.currentConfigItem.id
        })

        // Refresh data
        await this.initialize()
        const updatedItem = this.llmTags.find(t => t.id === this.currentConfigItem.id)
        if (updatedItem) {
          this.currentConfigurations = updatedItem.engine_configurations || []
        }
        this.newConfigEngineId = null

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Engine configuration added'
        })
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to add configuration'
        })
      } finally {
        this.addConfigLoading = false
      }
    },

    async deleteEngineConfig (configId) {
      try {
        await apiService.delete(`/admin/engine-llm-tag-configurations/${configId}`)

        // Refresh data
        await this.initialize()
        const updatedItem = this.llmTags.find(t => t.id === this.currentConfigItem.id)
        if (updatedItem) {
          this.currentConfigurations = updatedItem.engine_configurations || []
        }

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Engine configuration deleted'
        })
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete configuration'
        })
      }
    },

    // Bulk assign engine methods
    openBulkAssignEngineDialog () {
      this.bulkAssignEngineIds = []
      this.showBulkAssignEngineDialog = true
    },

    async submitBulkAssignEngines () {
      this.bulkAssignEngineLoading = true
      try {
        const tagIds = this.selectedItems.map(item => item.id)
        const response = await apiService.post('/admin/engine-llm-tag-configurations/bulk-assign', {
          tag_ids: tagIds,
          engine_ids: this.bulkAssignEngineIds
        })

        this.showBulkAssignEngineDialog = false
        this.selectedItems = []
        await this.initialize()

        const created = response.data.created_count || 0
        const skipped = response.data.skipped_count || 0

        Swal.fire({
          icon: 'success',
          title: 'Success',
          html: `<p>${created} engine configuration(s) created.</p><p>${skipped} skipped (already exist).</p>`
        })
      } catch (error) {
        console.error('Bulk assign engines error:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to assign engines'
        })
      } finally {
        this.bulkAssignEngineLoading = false
      }
    },

    // Inherit Docker defaults methods
    openInheritDialog () {
      this.inheritEngineId = null
      this.inheritMode = 'copies'
      this.inheritPreview = null
      this.showInheritDialog = true
    },

    async requestInherit (dryRun) {
      const response = await apiService.post('/admin/engine-llm-tag-configurations/inherit-docker-defaults', {
        engine_id: this.inheritEngineId,
        mode: this.inheritMode,
        dry_run: dryRun
      })
      return response.data
    },

    async previewInherit () {
      this.inheritPreview = null
      if (!this.inheritEngineId) return
      this.inheritLoading = true
      try {
        this.inheritPreview = await this.requestInherit(true)
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to preview'
        })
      } finally {
        this.inheritLoading = false
      }
    },

    async applyInherit () {
      if (this.inheritMode === 'all' && this.inheritPreview?.custom_rows) {
        const confirm = await Swal.fire({
          icon: 'warning',
          title: 'Remove per-model values?',
          text: `${this.inheritPreview.custom_rows} model(s) have values of their own. They will be removed and the Docker values used instead.`,
          showCancelButton: true,
          confirmButtonText: 'Remove',
          confirmButtonColor: '#d33'
        })
        if (!confirm.isConfirmed) return
      }
      this.inheritLoading = true
      try {
        const result = await this.requestInherit(false)
        this.showInheritDialog = false
        Swal.fire({
          icon: 'success',
          title: 'Done',
          text: `${result.changed_rows} model(s) now follow the Docker defaults.`
        })
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to apply'
        })
      } finally {
        this.inheritLoading = false
      }
    },

    // Manage engines methods
    openManageEnginesDialog () {
      this.resetEngineForm()
      this.showManageEnginesDialog = true
    },

    closeManageEnginesDialog () {
      this.showManageEnginesDialog = false
      this.resetEngineForm()
    },

    resetEngineForm () {
      this.engineForm = { id: null, name: '', icon: null }
    },

    editEngine (engine) {
      this.engineForm = { id: engine.id, name: engine.name, icon: null }
    },

    async submitEngineForm () {
      if (!this.engineForm.name) return
      this.engineFormLoading = true
      try {
        const formData = new FormData()
        formData.append('name', this.engineForm.name)
        if (this.engineForm.icon) {
          formData.append('icon', this.engineForm.icon)
        }

        if (this.engineForm.id) {
          await apiService.put(`/admin/engines/${this.engineForm.id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        } else {
          await apiService.post('/admin/engines', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        }

        this.resetEngineForm()
        await this.initialize()
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Engine saved successfully'
        })
      } catch (error) {
        console.error('Engine save error:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to save engine'
        })
      } finally {
        this.engineFormLoading = false
      }
    },

    async deleteEngine (engine) {
      const result = await Swal.fire({
        icon: 'warning',
        title: 'Delete Engine',
        html: `Are you sure you want to delete <strong>${engine.name}</strong>?<br><br>This may affect models configured with this engine.`,
        showCancelButton: true,
        confirmButtonColor: '#f44336',
        confirmButtonText: 'Yes, delete it'
      })

      if (!result.isConfirmed) return

      this.engineDeleteLoadingId = engine.id
      try {
        await apiService.delete(`/admin/engines/${engine.id}`)
        if (this.engineForm.id === engine.id) {
          this.resetEngineForm()
        }
        await this.initialize()
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Engine deleted successfully'
        })
      } catch (error) {
        console.error('Engine delete error:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to delete engine'
        })
      } finally {
        this.engineDeleteLoadingId = null
      }
    },

    // Migration methods
    openMigrateDialog () {
      this.showMigrateDialog = true
    },

    // Sparkrun allowlist methods
    async openSparkrunAllowlistDialog () {
      this.showSparkrunAllowlistDialog = true
      this.sparkrunAllowlistLoading = true
      try {
        const response = await apiService.get('/api/sparkrun/supported-engines')
        this.sparkrunEngines = response.data.engines || []
      } catch (error) {
        console.error('Failed to load sparkrun engines:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load sparkrun engine list'
        })
      } finally {
        this.sparkrunAllowlistLoading = false
      }
    },

    async saveSparkrunAllowlist () {
      this.sparkrunAllowlistSaving = true
      try {
        const supported = this.sparkrunEngines
          .filter(e => e.is_supported)
          .map(e => e.name)
        await apiService.put('/client-parameters', {
          sparkrun_supported_engines: JSON.stringify(supported)
        })
        this.showSparkrunAllowlistDialog = false
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Sparkrun engine allowlist updated'
        })
      } catch (error) {
        console.error('Failed to save sparkrun allowlist:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to save sparkrun engine allowlist'
        })
      } finally {
        this.sparkrunAllowlistSaving = false
      }
    },

    async submitMigration () {
      this.migrateLoading = true
      try {
        const response = await apiService.post('/admin/migrate-model-tags-to-llm-tags')

        if (response.data.ok) {
          this.showMigrateDialog = false
          await this.initialize()

          const createdTags = response.data.created_tags || 0
          const createdConfigs = response.data.created_configurations || 0
          const skippedTags = response.data.skipped_tags || 0
          const skippedConfigs = response.data.skipped_configurations || 0
          const errors = response.data.errors || []

          let detailsHtml = `
            <p><strong>Migration completed!</strong></p>
            <hr/>
            <ul style="text-align: left; list-style: none; padding-left: 0;">
              <li>✓ New LlmTags created: <strong>${createdTags}</strong></li>
              <li>✓ New configurations created: <strong>${createdConfigs}</strong></li>
              <li>⊘ Skipped tags: <strong>${skippedTags}</strong></li>
              <li>⊘ Skipped configurations: <strong>${skippedConfigs}</strong></li>
            </ul>
          `

          if (errors.length > 0) {
            detailsHtml += `
              <hr/>
              <p><strong>Errors (${errors.length}):</strong></p>
              <ul style="text-align: left; font-size: 12px; max-height: 150px; overflow-y: auto;">
                ${errors.slice(0, 10).map(e => `<li>${e}</li>`).join('')}
                ${errors.length > 10 ? `<li>... and ${errors.length - 10} more</li>` : ''}
              </ul>
            `
          }

          Swal.fire({
            icon: 'success',
            title: 'Migration Complete',
            html: detailsHtml,
            width: 600
          })
        } else {
          throw new Error(response.data.message || 'Unknown error')
        }
      } catch (error) {
        console.error('Migration error:', error)
        Swal.fire({
          icon: 'error',
          title: 'Migration Failed',
          text: error.response?.data?.message || error.message || 'Failed to migrate data.'
        })
      } finally {
        this.migrateLoading = false
      }
    },

    // Bulk status toggle methods
    async bulkSetDisabled (value) {
      const actionText = value ? 'disable' : 'activate'
      const result = await Swal.fire({
        icon: 'question',
        title: `Confirm ${value ? 'Disable' : 'Activate'}`,
        text: `Are you sure you want to ${actionText} ${this.selectedItems.length} model(s)?`,
        showCancelButton: true,
        confirmButtonColor: value ? '#f44336' : '#4caf50',
        confirmButtonText: `Yes, ${actionText} them`
      })

      if (!result.isConfirmed) return

      try {
        const ids = this.selectedItems.map(item => item.id)
        await apiService.put('/admin/llm-tags/bulk-set-field', {
          ids,
          field: 'is_disabled',
          value
        })

        const count = this.selectedItems.length
        this.selectedItems = []
        await this.initialize()
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: `Successfully ${value ? 'disabled' : 'activated'} ${count} model(s)`
        })
      } catch (error) {
        console.error('Bulk set disabled error:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: `Failed to ${actionText} models`
        })
      }
    },

    async bulkSetGated (value) {
      const actionText = value ? 'set as gated' : 'set as open'
      const result = await Swal.fire({
        icon: 'question',
        title: `Confirm ${value ? 'Gated' : 'Open'}`,
        text: `Are you sure you want to ${actionText} ${this.selectedItems.length} model(s)?`,
        showCancelButton: true,
        confirmButtonColor: value ? '#ff9800' : '#4caf50',
        confirmButtonText: `Yes, ${actionText}`
      })

      if (!result.isConfirmed) return

      try {
        const ids = this.selectedItems.map(item => item.id)
        await apiService.put('/admin/llm-tags/bulk-set-field', {
          ids,
          field: 'gated',
          value
        })

        const count = this.selectedItems.length
        this.selectedItems = []
        await this.initialize()
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: `Successfully ${value ? 'set as gated' : 'set as open'} ${count} model(s)`
        })
      } catch (error) {
        console.error('Bulk set gated error:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: `Failed to ${actionText} models`
        })
      }
    }
  }
}
</script>

<style scoped>
.v-data-table {
  border-radius: 8px;
}

.v-chip {
  font-weight: 500;
}

.bulk-item-row {
  background-color: #fafafa;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.bulk-item-row:hover {
  background-color: #f0f0f0;
}
</style>
